(() => {
    const collectionScript = document.currentScript;
    if (!collectionScript) return;
    if (window.__mainCollectionAssetInitialized) return;
    window.__mainCollectionAssetInitialized = true;
    const collectionConfig = collectionScript.dataset;

    const COLLECTION_MOBILE_BREAKPOINT = 750;
    let allCollectionHydrationPromise = null;
    let allCollectionHydrated = false;
    let lastAccordionMobileState = window.innerWidth < COLLECTION_MOBILE_BREAKPOINT;

    function normalizeFilterText(value = '') {
        return value.replace(/\s+/g, ' ').trim();
    }

    function getPreferredFacetsForm() {
        return document.querySelector('[is="facets-form"][update-on-change]') || document.querySelector('[is="facets-form"]');
    }

    function getFacetInputLabel(input) {
        if (!input) return '';

        const directLabel = input.dataset.filterLabel;
        if (directLabel) return normalizeFilterText(directLabel);

        if (input.id) {
            const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            if (label) return normalizeFilterText(label.textContent || '');
        }

        return normalizeFilterText(input.closest('label, .checkbox-control')?.textContent || '');
    }

    function buildFacetNavigationUrl(form) {
        const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
        const params = new URLSearchParams(new FormData(form));

        url.search = '';

        params.forEach((value, key) => {
            if (value !== '') {
                url.searchParams.append(key, value);
            }
        });

        if (!url.searchParams.get('page')) {
            url.searchParams.delete('page');
        }

        ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
            if (url.searchParams.get(key) === '') {
                url.searchParams.delete(key);
            }
        });

        return url;
    }

    function navigateWithFacetsForm(syncCallback, fallbackCallback) {
        const facetsForm = getPreferredFacetsForm();

        if (!facetsForm) {
            fallbackCallback?.();
            return;
        }

        const syncResult = syncCallback(facetsForm);
        if (syncResult === false) {
            fallbackCallback?.();
            return;
        }

        window.location.href = buildFacetNavigationUrl(facetsForm).toString();
    }

    function sanitizePriceFilterValue(value) {
        const normalized = normalizeFilterText(value);
        return normalized === '制限なし' ? '' : normalized;
    }

    function buildDirectFilterUrl(mutator) {
        const url = new URL(window.location.href);
        mutator(url.searchParams);
        return url;
    }

    function isAllCollectionPage() {
        return collectionConfig.collectionHandle === 'all';
    }

    function hasMetaobjectDrivenCollectionFilters() {
        return collectionConfig.metafieldFilters === 'true';
    }

    function getSortLabel(sortBy) {
        const sortLabelMap = {
            'manual': 'おすすめ順',
            'best-selling': '人気順',
            'title-ascending': '商品名順',
            'title-descending': '商品名逆順',
            'price-ascending': '価格の安い順',
            'price-descending': '価格の高い順',
            'created-ascending': '古い順',
            'created-descending': '新着順'
        };

        if (sortLabelMap[sortBy]) {
            return sortLabelMap[sortBy];
        }

        const matchingOption = document.querySelector(`#sort-by-popover [role="option"][value="${CSS.escape(sortBy)}"] .reversed-link`);
        return matchingOption ? normalizeFilterText(matchingOption.textContent || '') : '並び替え';
    }

    function getAllCollectionFilterState(params = new URLSearchParams(window.location.search)) {
        const activeSortBy = params.get('sort_by') || params.get('client_sort_by') || '';

        return {
            large: params.getAll('filter.p.m.custom._category1'),
            middle: params.getAll('filter.p.m.custom._category2'),
            minPrice: sanitizePriceFilterValue(params.get('filter.v.price.gte') || ''),
            maxPrice: sanitizePriceFilterValue(params.get('filter.v.price.lte') || ''),
            availableOnly: params.get('filter.v.availability') === '1',
            page: Math.max(1, Number(params.get('client_page') || params.get('page') || '1') || 1),
            sortBy: activeSortBy
        };
    }

    function hasAllCollectionCategoryFilters(params = new URLSearchParams(window.location.search)) {
        return params.getAll('filter.p.m.custom._category1').length > 0
            || params.getAll('filter.p.m.custom._category2').length > 0;
    }

    function hasAllCollectionPriceFilters(params = new URLSearchParams(window.location.search)) {
        return sanitizePriceFilterValue(params.get('filter.v.price.gte') || '') !== ''
            || sanitizePriceFilterValue(params.get('filter.v.price.lte') || '') !== '';
    }

    function shouldUseClientCollectionMode(state = getAllCollectionFilterState()) {
        return state.large.length > 0 || state.middle.length > 0;
    }

    function getAllCollectionServerPageCount() {
        const paginationRoot = document.getElementById('collectionServerPagination');
        if (!paginationRoot) return 1;

        const pageValues = Array.from(paginationRoot.querySelectorAll('[data-page]'))
            .map((node) => Number(node.getAttribute('data-page') || node.textContent || 0))
            .filter((value) => Number.isFinite(value) && value > 0);

        return Math.max(1, ...pageValues);
    }

    function buildAllCollectionServerPageUrl(page) {
        const url = new URL(window.location.href);
        url.searchParams.delete('client_page');
        url.searchParams.delete('client_sort_by');

        if (page <= 1) {
            url.searchParams.delete('page');
        } else {
            url.searchParams.set('page', String(page));
        }

        url.searchParams.set('section_id', collectionConfig.sectionId);
        return url.toString();
    }

    async function ensureAllCollectionCardsLoaded() {
        const productList = document.querySelector('.product-list');
        if (!productList) return [];

        if (allCollectionHydrated) {
            return Array.from(productList.querySelectorAll('.product-card'));
        }

        if (allCollectionHydrationPromise) {
            return allCollectionHydrationPromise;
        }

        const totalPages = getAllCollectionServerPageCount();
        if (totalPages <= 1) {
            allCollectionHydrated = true;
            return Array.from(productList.querySelectorAll('.product-card'));
        }

        allCollectionHydrationPromise = (async () => {
            const existingCards = Array.from(productList.querySelectorAll('.product-card'));
            const seenHandles = new Set(
                existingCards
                    .map((card) => card.querySelector('a')?.getAttribute('href')?.split('?')[0])
                    .filter(Boolean)
            );

            const pages = Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) => index + 2);
            const pageResults = await Promise.all(
                pages.map(async (page) => {
                    const response = await fetch(buildAllCollectionServerPageUrl(page), { credentials: 'same-origin' });
                    const html = await response.text();
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    return { page, cards: Array.from(doc.querySelectorAll('.product-card')) };
                })
            );

            pageResults
                .sort((resultA, resultB) => resultA.page - resultB.page)
                .forEach(({ page, cards }) => {
                    cards.forEach((card, index) => {
                        const handle = card.querySelector('a')?.getAttribute('href')?.split('?')[0] || `page-${page}-card-${index}`;
                        if (seenHandles.has(handle)) return;
                        seenHandles.add(handle);
                        productList.appendChild(card.cloneNode(true));
                    });
                });

            allCollectionHydrated = true;
            return Array.from(productList.querySelectorAll('.product-card'));
        })().catch((error) => {
            console.error('Failed to hydrate all collection products', error);
            return Array.from(productList.querySelectorAll('.product-card'));
        }).finally(() => {
            allCollectionHydrationPromise = null;
        });

        return allCollectionHydrationPromise;
    }

    function syncAllCollectionPaginationMode(useClientMode) {
        const serverPagination = document.getElementById('collectionServerPagination');
        const clientPagination = document.getElementById('collectionClientPagination');

        if (serverPagination) {
            serverPagination.hidden = useClientMode;
        }

        if (clientPagination && !useClientMode) {
            clientPagination.innerHTML = '';
            clientPagination.hidden = true;
        }
    }

    function hasCustomAllCollectionFilters(params = new URLSearchParams(window.location.search)) {
        return hasAllCollectionCategoryFilters(params)
            || hasAllCollectionPriceFilters(params)
            || params.get('filter.v.availability') === '1';
    }

    function shouldUseClientSort(sortBy) {
        return sortBy === 'price-ascending' || sortBy === 'price-descending';
    }

    function shouldUseClientSortForAllCollectionFilters(state = getAllCollectionFilterState()) {
        return shouldUseClientSort(state.sortBy)
            && (shouldUseClientCollectionMode(state) || state.minPrice !== '' || state.maxPrice !== '');
    }

    function shouldUseClientRenderedCollectionFilters(state = getAllCollectionFilterState()) {
        if (isAllCollectionPage()) {
            // Shopify has already applied category, price and availability filters
            // before rendering this page. Keep client mode only for filtered price
            // sorting, whose variant-aware order intentionally differs from Shopify's.
            return shouldUseClientSortForAllCollectionFilters(state);
        }

        if (!hasMetaobjectDrivenCollectionFilters()) {
            return false;
        }

        return state.large.length > 0
            || state.middle.length > 0
            || state.minPrice !== ''
            || state.maxPrice !== ''
            || state.availableOnly;
    }

    function sortClientFilteredCards(cards, sortBy) {
        if (!shouldUseClientSort(sortBy)) return cards;

        return [...cards].sort((cardA, cardB) => {
            const priceKey = sortBy === 'price-descending' ? 'priceMax' : 'priceMin';
            const priceA = Number(cardA.dataset[priceKey] || cardA.dataset.price || 0);
            const priceB = Number(cardB.dataset[priceKey] || cardB.dataset.price || 0);

            if (priceA === priceB) return 0;

            return sortBy === 'price-descending'
                ? priceB - priceA
                : priceA - priceB;
        });
    }

    function syncAllCollectionSortUi() {
        if (!isAllCollectionPage()) return;

        const { sortBy } = getAllCollectionFilterState();
        if (!sortBy) return;

        document.querySelectorAll('#sort-by-popover [role="option"]').forEach((option) => {
            option.setAttribute('aria-selected', option.getAttribute('value') === sortBy ? 'true' : 'false');
        });

        const currentLabel = getSortLabel(sortBy);
        document.querySelectorAll('.collection-toolbar__sort-button .text-with-icon').forEach((buttonLabel) => {
            const icon = buttonLabel.querySelector('.icon');
            buttonLabel.textContent = currentLabel;
            if (icon) {
                buttonLabel.appendChild(icon);
            }
        });
    }

    function renderClientPagination({ totalItems, currentPage, perPage, pageParam = 'client_page' }) {
        const container = document.getElementById('collectionClientPagination');
        if (!container) return;

        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        if (totalItems === 0 || totalPages <= 1) {
            container.innerHTML = '';
            container.hidden = true;
            return;
        }

        const createPageUrl = (page) => {
            const url = buildDirectFilterUrl((params) => {
                params.delete('page');
                params.delete('client_page');
                if (page <= 1) {
                    params.delete(pageParam);
                } else {
                    params.set(pageParam, String(page));
                }
            });

            return url.toString();
        };

        const pageParts = [];

        if (totalPages <= 7) {
            for (let page = 1; page <= totalPages; page += 1) {
                pageParts.push(page);
            }
        } else if (currentPage <= 4) {
            pageParts.push(1, 2, 3, 4, 5, 'ellipsis', totalPages);
        } else if (currentPage >= totalPages - 3) {
            pageParts.push(1, 'ellipsis');
            for (let page = totalPages - 4; page <= totalPages; page += 1) {
                pageParts.push(page);
            }
        } else {
            pageParts.push(1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
        }

        const parts = pageParts.map((part) => {
            if (part === 'ellipsis') {
                return '<span class="pagination__link pagination__item--ellipsis h6" aria-hidden="true">…</span>';
            }

            const page = part;
            return page === currentPage
                ? `<span class="pagination__link pagination__link--disabled h6" aria-current="page" data-page="${page}">${page}</span>`
                : `<a class="pagination__link h6" aria-label="ページ ${page}" href="${createPageUrl(page)}" data-page="${page}">${page}</a>`;
        });

        container.hidden = false;
        container.innerHTML = `
            <nav class="pagination" role="navigation" aria-label="パジネーション">
                ${totalPages >= 7 && currentPage > 1 ? `
                    <a class="pagination__link pagination__item--prev h6" rel="prev" aria-label="ページ ${currentPage - 1}" href="${createPageUrl(currentPage - 1)}">
                        <svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.8405 24.9002L1.31811 12.8831L16.0923 0.647455" stroke="#313131" stroke-width="1.68132"></path>
                        </svg>
                    </a>
                ` : ''}
                ${parts.join('')}
                ${totalPages >= 7 && currentPage < totalPages ? `
                    <a class="pagination__link pagination__item--next h6" rel="next" aria-label="ページ ${currentPage + 1}" href="${createPageUrl(currentPage + 1)}">
                        <svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg" style="transform: scaleX(-1);">
                            <path d="M15.8405 24.9002L1.31811 12.8831L16.0923 0.647455" stroke="#313131" stroke-width="1.68132"></path>
                        </svg>
                    </a>
                ` : ''}
            </nav>
        `;

        window.initAllPaginations?.();
    }

    function updateCollectionToolbarProductsCount(count) {
        document.querySelectorAll('.collection-toolbar__products-count').forEach((countLabel) => {
            const template = countLabel.dataset.productsCountTemplate;

            if (template) {
                countLabel.textContent = template.replace('__COUNT__', String(count));
                return;
            }

            countLabel.textContent = `${count}件の商品`;
        });
    }

    function matchesCategoryFilter(card, selectedValues, dataKey) {
        if (selectedValues.length === 0) return true;
        const rawValue = card.dataset[dataKey] || '';
        if (!rawValue) return false;
        const values = rawValue.split('|').filter(Boolean);
        return selectedValues.some((value) => values.includes(value));
    }

    async function applyClientSideCollectionFilters() {
        const productList = document.querySelector('.product-list');
        if (!productList) return false;

        const state = getAllCollectionFilterState();
        const useClientMode = shouldUseClientRenderedCollectionFilters(state);
        const useServerBackedClientPagination = isAllCollectionPage()
            && shouldUseClientCollectionMode(state)
            && !useClientMode;

        syncAllCollectionPaginationMode(useClientMode || useServerBackedClientPagination);

        if (!useClientMode) {
            if (useServerBackedClientPagination) {
                const totalItems = Number(productList.dataset.totalProducts || 0);
                const perPage = Number(productList.dataset.productsPerPage || Number(collectionConfig.productsPerPage));

                renderClientPagination({
                    totalItems,
                    currentPage: state.page,
                    perPage,
                    pageParam: 'page'
                });
            }

            return false;
        }

        await ensureAllCollectionCardsLoaded();

        const cards = Array.from(productList.querySelectorAll('.product-card'));
        if (cards.length === 0) return false;
        const hasCustomFilters = state.large.length > 0 || state.middle.length > 0 || state.minPrice !== '' || state.maxPrice !== '' || state.availableOnly;

        const minPrice = state.minPrice === '' ? null : Number(state.minPrice) * 100;
        const maxPrice = state.maxPrice === '' ? null : Number(state.maxPrice) * 100;
        const perPage = Number(collectionConfig.productsPerPage);
        const filteredCards = [];

        cards.forEach((card) => {
            const priceMin = Number(card.dataset.priceMin || card.dataset.price || 0);
            const priceMax = Number(card.dataset.priceMax || card.dataset.price || 0);
            const isAvailable = card.dataset.available === 'true';
            const matchesLarge = matchesCategoryFilter(card, state.large, 'category1Gid');
            const matchesMiddle = matchesCategoryFilter(card, state.middle, 'category2Gids');
            const matchesPriceMin = minPrice === null || priceMax >= minPrice;
            const matchesPriceMax = maxPrice === null || priceMin <= maxPrice;
            const matchesAvailability = !state.availableOnly || isAvailable;
            const isMatch = !hasCustomFilters || (matchesLarge && matchesMiddle && matchesPriceMin && matchesPriceMax && matchesAvailability);

            card.hidden = true;
            if (isMatch) filteredCards.push(card);
        });

        const sortedFilteredCards = sortClientFilteredCards(filteredCards, state.sortBy);
        const hiddenCards = cards.filter((card) => !sortedFilteredCards.includes(card));

        if (shouldUseClientSort(state.sortBy)) {
            productList.append(...sortedFilteredCards, ...hiddenCards);
        }

        const totalItems = sortedFilteredCards.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        const currentPage = Math.min(state.page, totalPages);
        const startIndex = (currentPage - 1) * perPage;
        const endIndex = startIndex + perPage;

        sortedFilteredCards.forEach((card, index) => {
            card.hidden = index < startIndex || index >= endIndex;
        });

        const emptyState = document.getElementById('collectionClientEmptyState');
        if (emptyState) {
            emptyState.hidden = totalItems !== 0;
        }

        updateCollectionToolbarProductsCount(totalItems);

        renderClientPagination({ totalItems, currentPage, perPage });
        renderActiveFilterTags();
        return true;
    }

    function getCategoryInputLabel(input) {
        return normalizeFilterText(input.closest('label, .title, .chip')?.textContent || '');
    }

    function clearFacetCategoryInputs(facetsForm, categoryLabels) {
        facetsForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
            const inputLabel = getFacetInputLabel(input);
            const isCategoryName = input.name.includes('_category1') || input.name.includes('_category2');
            if (isCategoryName || (inputLabel && categoryLabels.has(inputLabel))) {
                input.checked = false;
            }
        });
    }

    function findMatchingFacetInput(facetsForm, { preferredName, value, label }) {
        const escapedValue = CSS.escape(value);
        const candidates = [];

        if (preferredName) {
            candidates.push(`input[type="checkbox"][name="${preferredName}"][value="${escapedValue}"]`);
        }

        candidates.push(`input[type="checkbox"][value="${escapedValue}"]`);

        for (const selector of candidates) {
            const match = facetsForm.querySelector(selector);
            if (match) return match;
        }

        if (!label) return null;

        const normalizedLabel = normalizeFilterText(label);
        return Array.from(facetsForm.querySelectorAll('input[type="checkbox"]')).find((input) => {
            if (preferredName && input.name !== preferredName && !input.name.includes('_category')) {
                return false;
            }

            return getFacetInputLabel(input) === normalizedLabel;
        }) || null;
    }

    // facetsフォーム内の価格number inputのstepバリデーションを無効化
    function disablePriceStepValidation() {
        document.querySelectorAll('[is="facets-form"] input[type="number"][name*="filter.v.price"]').forEach(input => {
            input.setAttribute('step', 'any');
        });
    }
    document.addEventListener("DOMContentLoaded", () => disablePriceStepValidation());
    document.addEventListener("shopify:section:load", () => disablePriceStepValidation());

    // ツールバーをスクロール時に画面上部に固定する
    let _stickyCleanup = null;

    function initStickyToolbar() {
        if (_stickyCleanup) {
            _stickyCleanup();
            _stickyCleanup = null;
        }

        const toolbar = document.querySelector('.collection-toolbar');
        if (!toolbar) return;

        let placeholder = toolbar.nextElementSibling;
        if (!placeholder || !placeholder.classList.contains('sticky-toolbar-placeholder')) {
            placeholder = document.createElement('div');
            placeholder.classList.add('sticky-toolbar-placeholder');
            placeholder.style.display = 'none';
            toolbar.parentNode.insertBefore(placeholder, toolbar.nextSibling);
        }

        function getStickyOffset() {
            const rootStyles = getComputedStyle(document.documentElement);
            const headerContainer = document.querySelector('.mp-header__container');
            const headerContainerHeight = headerContainer ? headerContainer.getBoundingClientRect().height : 0;

            if (headerContainerHeight > 0) {
                return Math.ceil(headerContainerHeight);
            }

            const mpHeaderHeight = parseFloat(rootStyles.getPropertyValue('--mp-header-height')) || 0;

            if (mpHeaderHeight > 0) {
                return Math.ceil(mpHeaderHeight);
            }

            const stickyAreaHeight = parseFloat(rootStyles.getPropertyValue('--sticky-area-height')) || 0;
            return Math.ceil(stickyAreaHeight);
        }

        function setFixedStyles(isMobileView, toolbarHeight) {
            toolbar.classList.add('is-fixed');
            toolbar.style.top = `${getStickyOffset()}px`;
            toolbar.style.height = `${toolbarHeight}px`;

            if (isMobileView) {
                toolbar.style.left = '0';
                toolbar.style.width = '100vw';
                toolbar.style.maxWidth = 'none';
                toolbar.style.transform = 'none';
            }
        }

        function clearFixedStyles(isMobileView) {
            toolbar.classList.remove('is-fixed');
            toolbar.style.top = '';
            toolbar.style.height = '';

            if (isMobileView) {
                toolbar.style.left = '';
                toolbar.style.width = '';
                toolbar.style.maxWidth = '';
                toolbar.style.transform = '';
            }
        }

        let isFixed = false;
        let stickyFrame = null;

        function updateStickyState() {
            const isMobileView = window.innerWidth < COLLECTION_MOBILE_BREAKPOINT;

            if (isMobileView) {
                if (isFixed) {
                    clearFixedStyles(true);
                    isFixed = false;
                }

                placeholder.style.display = 'none';
                placeholder.style.height = '';
                return;
            }

            const stickyOffset = getStickyOffset();
            const toolbarTop = isFixed
                ? placeholder.getBoundingClientRect().top + window.scrollY
                : toolbar.getBoundingClientRect().top + window.scrollY;
            const triggerPoint = toolbarTop - stickyOffset;

            if (window.scrollY >= triggerPoint && !isFixed) {
                const toolbarHeight = toolbar.offsetHeight;
                placeholder.style.display = 'block';
                placeholder.style.height = `${toolbarHeight}px`;
                setFixedStyles(isMobileView, toolbarHeight);
                isFixed = true;
            } else if (window.scrollY < triggerPoint && isFixed) {
                clearFixedStyles(isMobileView);
                placeholder.style.display = 'none';
                isFixed = false;
            } else if (isFixed) {
                toolbar.style.top = `${stickyOffset}px`;
            }
        }

        function requestStickyUpdate() {
            if (stickyFrame !== null) return;

            stickyFrame = window.requestAnimationFrame(() => {
                stickyFrame = null;
                updateStickyState();
            });
        }

        window.addEventListener('scroll', requestStickyUpdate, { passive: true });
        window.addEventListener('resize', requestStickyUpdate);

        _stickyCleanup = () => {
            window.removeEventListener('scroll', requestStickyUpdate);
            window.removeEventListener('resize', requestStickyUpdate);
            if (stickyFrame !== null) {
                window.cancelAnimationFrame(stickyFrame);
                stickyFrame = null;
            }
        };

        updateStickyState();
    }
    function initAllFilterAccordions() {
        document.querySelectorAll('[data-accordion-collapsible], #categoryForm').forEach((form) => {
            initCategoryAccordion(form);
        });
    }

    window.addEventListener('resize', () => {
        const nextMobileState = window.innerWidth < COLLECTION_MOBILE_BREAKPOINT;
        if (nextMobileState === lastAccordionMobileState) return;

        lastAccordionMobileState = nextMobileState;
        initAllFilterAccordions();
    });

    // 在庫ありフィルター
    function initAvailabilityFilter() {
        const checkbox = document.getElementById('availability-filter');
        if (!checkbox || checkbox._availabilityBound) return;
        checkbox._availabilityBound = true;

        // URLから初期状態を復元
        const params = new URLSearchParams(window.location.search);
        if (params.get('filter.v.availability') === '1') {
            checkbox.checked = true;
        }

        checkbox.addEventListener('change', () => {
            if (isAllCollectionPage()) {
                const url = buildDirectFilterUrl((params) => {
                    params.delete('page');
                    params.delete('client_page');
                    if (checkbox.checked) {
                        params.set('filter.v.availability', '1');
                    } else {
                        params.delete('filter.v.availability');
                    }
                });
                window.location.href = url.toString();
                return;
            }

            navigateWithFacetsForm((facetsForm) => {
                const availabilityInput = facetsForm.querySelector('input[type="checkbox"][name="filter.v.availability"][value="1"]');
                if (!availabilityInput) return false;
                availabilityInput.checked = checkbox.checked;
            }, () => {
                const url = new URL(window.location.href);
                if (checkbox.checked) {
                    url.searchParams.set('filter.v.availability', '1');
                } else {
                    url.searchParams.delete('filter.v.availability');
                }
                window.location.href = url.toString();
            });
        });
    }

    // グリッド列切替
    function initGridSwitcher() {
        const productList = document.querySelector('.product-list');
        if (!productList) return;

        const desktopKey = 'grid-cols-desktop';
        const mobileKey = 'grid-cols-mobile';

        function isMobile() {
            return window.innerWidth < 750;
        }

        function applyGrid(cols) {
            productList.style.setProperty('grid-template-columns', `repeat(${cols}, minmax(0, 1fr))`, 'important');
        }

        function setActive(container, cols) {
            container.querySelectorAll('.grid-switcher__btn').forEach(btn => {
                btn.classList.toggle('is-active', btn.dataset.cols === String(cols));
            });
        }

        function applyCurrent() {
            if (isMobile()) {
                const cols = sessionStorage.getItem(mobileKey) || '2';
                applyGrid(cols);
                document.querySelectorAll('.grid-switcher--mobile').forEach(c => setActive(c, cols));
            } else {
                const cols = sessionStorage.getItem(desktopKey) || '3';
                applyGrid(cols);
                document.querySelectorAll('.grid-switcher--desktop').forEach(c => setActive(c, cols));
            }
        }

        // ボタンクリックイベント
        document.querySelectorAll('.grid-switcher__btn').forEach(btn => {
            if (btn._gridBound) return;
            btn._gridBound = true;
            btn.addEventListener('click', () => {
                const cols = btn.dataset.cols;
                const key = isMobile() ? mobileKey : desktopKey;
                sessionStorage.setItem(key, cols);
                applyGrid(cols);
                // 同じデバイスカテゴリのボタンだけ更新
                const container = btn.closest('.grid-switcher');
                if (container) setActive(container, cols);
            });
        });

        // 初期適用
        applyCurrent();

        // resize時にデスクトップ↔モバイル切替
        let wasMobileState = isMobile();
        window.addEventListener('resize', () => {
            const nowMobile = isMobile();
            if (wasMobileState !== nowMobile) {
                wasMobileState = nowMobile;
                applyCurrent();
            }
        });
    }

    function getCategoryParamValues(params = new URLSearchParams(window.location.search)) {
        return {
            large: params.getAll('filter.p.m.custom._category1'),
            middle: params.getAll('filter.p.m.custom._category2')
        };
    }

    // 選択中カテゴリをタグとして表示
    function renderActiveFilterTags() {
        const container = document.getElementById('activeFilters');
        if (!container) return;
        container.innerHTML = '';

        if (hasMetaobjectDrivenCollectionFilters()) {
            const params = new URLSearchParams(window.location.search);
            const customForms = Array.from(document.querySelectorAll('[data-custom-filter-form]'));
            const tagEntries = [];

            customForms.forEach((form) => {
                (window.MonotomoiCustomCollectionFilters?.getPriceRanges(form) || []).forEach(({ filterLabel, minParam, maxParam }) => {
                    const minValue = sanitizePriceFilterValue(params.get(minParam || '') || '');
                    const maxValue = sanitizePriceFilterValue(params.get(maxParam || '') || '');

                    if (minValue || maxValue) {
                        const priceLabel = `${filterLabel}: ${minValue || '0'}円〜${maxValue || '上限なし'}円`;
                        tagEntries.push({
                            label: priceLabel,
                            remove: () => {
                                const nextParams = new URLSearchParams(window.location.search);
                                nextParams.delete(minParam);
                                nextParams.delete(maxParam);
                                nextParams.delete('page');
                                nextParams.delete('client_page');
                                window.location.search = nextParams.toString();
                            }
                        });
                    }
                });

                form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
                    if (input.dataset.categoryLevel === 'large') return;
                    tagEntries.push({
                        label: input.dataset.filterChipLabel || input.value,
                        remove: () => {
                            const nextParams = new URLSearchParams(window.location.search);
                            const remainingValues = nextParams.getAll(input.name).filter((value) => value !== input.value);
                            nextParams.delete(input.name);
                            remainingValues.forEach((value) => nextParams.append(input.name, value));
                            nextParams.delete('page');
                            nextParams.delete('client_page');
                            window.location.search = nextParams.toString();
                        }
                    });
                });
            });

            tagEntries.forEach((entry) => {
                const tag = document.createElement('button');
                tag.type = 'button';
                tag.className = 'active-filter-tag';
                tag.innerHTML = `<span class="active-filter-tag__close">✕</span>${entry.label}`;
                tag.addEventListener('click', entry.remove);
                container.appendChild(tag);
            });

            if (tagEntries.length > 0) {
                return;
            }
        }

        const { middle } = getCategoryParamValues();
        const selectedValues = [...new Set(middle)];
        if (selectedValues.length === 0) return;

        const gidToLabel = {};
        document.querySelectorAll('#categoryForm input[data-category-level]').forEach(input => {
            const label = input.closest('label') || input.parentElement?.querySelector('label, span');
            if (label) gidToLabel[input.value] = label.textContent.trim();
        });

        selectedValues.forEach(gid => {
            const label = gidToLabel[gid] || gid;
            const tag = document.createElement('button');
            tag.type = 'button';
            tag.className = 'active-filter-tag';
            tag.innerHTML = '<span class="active-filter-tag__close">✕</span>' + label;
            tag.addEventListener('click', () => {
                const p = new URLSearchParams(window.location.search);
                ['filter.p.m.custom._category1', 'filter.p.m.custom._category2'].forEach((key) => {
                    const remaining = p.getAll(key).filter(v => v !== gid);
                    p.delete(key);
                    remaining.forEach(v => p.append(key, v));
                });
                p.delete('page');
                p.delete('client_page');
                window.location.search = p.toString();
            });
            container.appendChild(tag);
        });
    }

    // 並び替え選択時にフルページリロードで遷移（AJAX更新だとメタフィールドが壊れるため）
    function interceptSortForFullReload() {
        document.addEventListener('facet:update', (e) => {
            const url = e.detail && e.detail.url;
            if (url && url.searchParams.has('sort_by')) {
                e.stopImmediatePropagation();
                const reloadUrl = new URL(url);
                reloadUrl.searchParams.delete('section_id');
                if (isAllCollectionPage() && (hasAllCollectionCategoryFilters(reloadUrl.searchParams) || hasAllCollectionPriceFilters(reloadUrl.searchParams))) {
                    reloadUrl.searchParams.delete('page');
                    reloadUrl.searchParams.delete('client_page');

                    if (shouldUseClientSort(reloadUrl.searchParams.get('sort_by'))) {
                        reloadUrl.searchParams.set('client_sort_by', reloadUrl.searchParams.get('sort_by'));
                        reloadUrl.searchParams.delete('sort_by');
                    } else {
                        reloadUrl.searchParams.delete('client_sort_by');
                    }
                }
                window.location.href = reloadUrl.toString();
            }
        }, true);
    }

    function initCollectionInteractions({ includeSticky = false, includeActiveTags = false } = {}) {
        if (includeSticky) {
            initStickyToolbar();
        }

        bindModalEvents();

        if (document.getElementById('tagPriceForm')) {
            tagPriceFilter();
        }

        window.MonotomoiCollectionCustomFilterDeps = {
            normalizeFilterText,
            sanitizePriceFilterValue,
            buildDirectFilterUrl,
            setCategoryAccordionOpen,
            mobileBreakpoint: COLLECTION_MOBILE_BREAKPOINT
        };
        window.MonotomoiCustomCollectionFilters?.init();

        if (document.getElementById('tagPriceForm') || document.getElementById('categoryForm')) {
            restoreFilter();
        }

        if (document.getElementById('categoryForm')) {
            bindCategoryForm();
        }

        initAllFilterAccordions();

        initAvailabilityFilter();
        initGridSwitcher();
        applyClientSideCollectionFilters();
        syncAllCollectionSortUi();
        window.MonotomoiCustomCollectionFilters?.syncCategoryCounts();

        if (includeActiveTags) {
            renderActiveFilterTags();
        }
    }

    document.addEventListener("DOMContentLoaded", () => {
        if (isAllCollectionPage()) {
            const currentUrl = new URL(window.location.href);
            const customSort = currentUrl.searchParams.get('client_sort_by');
            const currentSort = currentUrl.searchParams.get('sort_by');
            const hasCategoryFilters = hasAllCollectionCategoryFilters(currentUrl.searchParams);
            const hasPriceFilters = hasAllCollectionPriceFilters(currentUrl.searchParams);
            const usesClientPagination = (hasCategoryFilters || hasPriceFilters)
                && shouldUseClientSort(customSort || currentSort);
            const serverPage = currentUrl.searchParams.get('page');
            const clientPage = currentUrl.searchParams.get('client_page');

            if ((hasCategoryFilters || hasPriceFilters) && shouldUseClientSort(currentSort)) {
                currentUrl.searchParams.set('client_sort_by', currentSort);
                currentUrl.searchParams.delete('sort_by');
                currentUrl.searchParams.delete('page');
                currentUrl.searchParams.delete('client_page');
                window.location.replace(currentUrl.toString());
                return;
            }

            if (!hasCategoryFilters && !hasPriceFilters && shouldUseClientSort(customSort)) {
                currentUrl.searchParams.set('sort_by', customSort);
                currentUrl.searchParams.delete('client_sort_by');
                if (clientPage) {
                    currentUrl.searchParams.set('page', clientPage);
                } else {
                    currentUrl.searchParams.delete('page');
                }
                currentUrl.searchParams.delete('client_page');
                window.location.replace(currentUrl.toString());
                return;
            }

            if (usesClientPagination && serverPage && !clientPage) {
                currentUrl.searchParams.set('client_page', serverPage);
                currentUrl.searchParams.delete('page');
                window.location.replace(currentUrl.toString());
                return;
            }

            if (!usesClientPagination && clientPage) {
                currentUrl.searchParams.set('page', clientPage);
                currentUrl.searchParams.delete('client_page');
                window.location.replace(currentUrl.toString());
                return;
            }
        }

        initCollectionInteractions({ includeSticky: true, includeActiveTags: true });
        interceptSortForFullReload();
    });

    document.addEventListener("shopify:section:load", () => {
        initCollectionInteractions({ includeSticky: true });
    });

    // モーダルの開閉イベントのセット
    function bindModalEvents() {
        const openButtons = document.querySelectorAll(".modal-open-btn");
        const closeButtons = document.querySelectorAll(".modal-close-btn");

        openButtons.forEach(btn => {
            btn.removeEventListener("click", btn._modalHandler || (() => {})); // 重複防止
            const handler = () => {
                const modal = document.getElementById(btn.dataset.target);
                if (modal) {
                    modal.classList.add("show");
                    modal.scrollTop = 0;
                    modal.querySelector('.modal-content')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    modal.querySelector('.category-accordion')?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                }
            };
            btn._modalHandler = handler;
            btn.addEventListener("click", handler);
        });

        closeButtons.forEach(btn => {
            btn.removeEventListener("click", btn._modalHandler || (() => {}));
            const handler = () => {
                const modal = btn.closest(".modal");
                if (modal) modal.classList.remove("show");
            };
            btn._modalHandler = handler;
            btn.addEventListener("click", handler);
        });

        document.querySelectorAll(".modal").forEach(modal => {
            modal.removeEventListener("click", modal._modalHandler || (() => {}));
            const handler = (e) => {
                if (e.target === modal) modal.classList.remove("show");
            };
            modal._modalHandler = handler;
            modal.addEventListener("click", handler);
        });
    }

    function tagPriceFilter() {
        const form = document.getElementById('tagPriceForm');
        if (!form || form._bound) return;

        const applyPriceFilters = () => {
            if (isAllCollectionPage()) {
                const url = buildDirectFilterUrl((params) => {
                    ['filter.v.price.gte', 'filter.v.price.lte', 'page', 'client_page'].forEach((key) => params.delete(key));
                    const min = sanitizePriceFilterValue(form.elements["filter.v.price.gte"]?.value || '');
                    const max = sanitizePriceFilterValue(form.elements["filter.v.price.lte"]?.value || '');
                    if (min) params.set("filter.v.price.gte", min);
                    if (max) params.set("filter.v.price.lte", max);
                });
                window.location.href = url.toString();
                return;
            }

            navigateWithFacetsForm((facetsForm) => {
                const min = sanitizePriceFilterValue(form.elements["filter.v.price.gte"]?.value || '');
                const max = sanitizePriceFilterValue(form.elements["filter.v.price.lte"]?.value || '');
                const minInput = facetsForm.querySelector('input[name="filter.v.price.gte"]');
                const maxInput = facetsForm.querySelector('input[name="filter.v.price.lte"]');

                if (!minInput && !maxInput) return false;
                if (minInput) minInput.value = min;
                if (maxInput) maxInput.value = max;
            }, () => {
                const url = buildDirectFilterUrl((params) => {
                    ['filter.v.price.gte', 'filter.v.price.lte', 'page', 'client_page'].forEach((key) => params.delete(key));
                    const min = sanitizePriceFilterValue(form.elements["filter.v.price.gte"]?.value || '');
                    const max = sanitizePriceFilterValue(form.elements["filter.v.price.lte"]?.value || '');
                    if (min) params.set("filter.v.price.gte", min);
                    if (max) params.set("filter.v.price.lte", max);
                });

                window.location.href = url.toString();
            });
        };

        const resetPriceFilters = () => {
            const min = form.elements["filter.v.price.gte"];
            const max = form.elements["filter.v.price.lte"];
            if (min) min.value = '';
            if (max) max.value = '';

            if (isAllCollectionPage()) {
                const url = buildDirectFilterUrl((params) => {
                    ['filter.v.price.gte', 'filter.v.price.lte', 'page', 'client_page'].forEach((key) => params.delete(key));
                });
                window.location.href = url.toString();
                return;
            }

            const url = buildDirectFilterUrl((params) => {
                ['filter.v.price.gte', 'filter.v.price.lte', 'page', 'client_page'].forEach((key) => params.delete(key));
            });

            window.location.href = url.toString();
        };

        form.addEventListener('submit', e => {
            e.preventDefault();
            applyPriceFilters();
        });

        form.querySelector('.active-button')?.addEventListener('click', applyPriceFilters);
        form.querySelector('.reset-button')?.addEventListener('click', resetPriceFilters);

        form._bound = true;
    }

    function bindCategoryLevelInputs(categoryForm) {
        if (!categoryForm || categoryForm._categoryLevelBound) return;

        categoryForm.querySelectorAll('input[data-category-level="large"]').forEach(largeCb => {
            largeCb.addEventListener('change', () => {
                const title = largeCb.closest('.title');
                const unit = title?.nextElementSibling;
                if (!unit) return;
                largeCb.indeterminate = false;
                unit.querySelectorAll('input[name="filter.p.m.custom._category2"]').forEach(midCb => {
                    midCb.checked = largeCb.checked;
                });
                if ((window.innerWidth < 750 || categoryForm.dataset.accordionCollapsible === 'true') && title) {
                    setCategoryAccordionOpen(title, largeCb.checked, categoryForm);
                }
            });
        });

        categoryForm.querySelectorAll('input[name="filter.p.m.custom._category2"]').forEach(midCb => {
            midCb.addEventListener('change', () => {
                const unit = midCb.closest('.unit');
                const title = unit ? unit.previousElementSibling : null;
                if (!title) return;
                const largeCb = title.querySelector('input[data-category-level="large"]');
                if (largeCb) {
                    const allMid = unit.querySelectorAll('input[name="filter.p.m.custom._category2"]');
                    const anyChecked = Array.from(allMid).some(cb => cb.checked);
                    const allChecked = Array.from(allMid).every(cb => cb.checked);
                    largeCb.checked = anyChecked;
                    largeCb.indeterminate = anyChecked && !allChecked;
                }
                if (window.innerWidth < 750 || categoryForm.dataset.accordionCollapsible === 'true') {
                    setCategoryAccordionOpen(title, true, categoryForm);
                }
            });
        });

        categoryForm._categoryLevelBound = true;
    }

    function getCategorySubmissionState(categoryForm) {
        const large = new Set();
        const middle = [];

        categoryForm.querySelectorAll('input[data-category-level="large"]:checked').forEach((largeCb) => {
            large.add(largeCb.value);
        });

        categoryForm.querySelectorAll('input[data-category-level="middle"]:checked').forEach((midCb) => {
            middle.push(midCb.value);
            if (midCb.dataset.categoryParentValue) {
                large.add(midCb.dataset.categoryParentValue);
            }
        });

        return {
            large: Array.from(large),
            middle
        };
    }

    function syncCategoryCheckboxTree(categoryForm) {
        if (!categoryForm) return;

        categoryForm.querySelectorAll('input[data-category-level="large"]').forEach((largeCb) => {
            const title = largeCb.closest('.title');
            const unit = title?.nextElementSibling;
            if (!unit) return;

            const middleInputs = unit.querySelectorAll('input[data-category-level="middle"]');
            if (middleInputs.length === 0) return;

            const anyChecked = Array.from(middleInputs).some((midCb) => midCb.checked);
            const allChecked = Array.from(middleInputs).every((midCb) => midCb.checked);

            if (anyChecked) {
                largeCb.checked = true;
                largeCb.indeterminate = !allChecked;
                return;
            }

            largeCb.indeterminate = false;
        });
    }

    function bindCategoryForm() {
        const categoryForm = document.getElementById('categoryForm');
        if (!categoryForm || categoryForm._bound) return;

        bindCategoryLevelInputs(categoryForm);

        const applyCategoryFilters = () => {
            const submissionState = getCategorySubmissionState(categoryForm);

            if (isAllCollectionPage()) {
                const url = buildDirectFilterUrl((params) => {
                    params.delete('filter.p.m.custom._category1');
                    params.delete('filter.p.m.custom._category2');
                    params.delete('page');
                    params.delete('client_page');

                    submissionState.large.forEach((value) => {
                        params.append('filter.p.m.custom._category1', value);
                    });

                    submissionState.middle.forEach((value) => {
                        params.append('filter.p.m.custom._category2', value);
                    });
                });
                window.location.href = url.toString();
                return;
            }

            navigateWithFacetsForm((facetsForm) => {
                const categoryInputs = Array.from(categoryForm.querySelectorAll('input[data-category-level]'));
                const categoryLabels = new Set(categoryInputs.map((input) => getCategoryInputLabel(input)).filter(Boolean));
                const selectedInputs = categoryInputs.filter((input) => input.checked);
                let matchedCount = 0;

                clearFacetCategoryInputs(facetsForm, categoryLabels);

                selectedInputs.forEach((input) => {
                        const facetInput = findMatchingFacetInput(facetsForm, {
                            preferredName: input.name,
                            value: input.value,
                            label: getCategoryInputLabel(input)
                        });

                        if (facetInput) {
                            facetInput.checked = true;
                            matchedCount += 1;
                        }
                    });

                if (selectedInputs.length > 0 && matchedCount === 0) {
                    return false;
                }
            }, () => {
                const url = buildDirectFilterUrl((params) => {
                    params.delete('filter.p.m.custom._category1');
                    params.delete('filter.p.m.custom._category2');
                    params.delete('page');
                    params.delete('client_page');

                    submissionState.large.forEach((value) => {
                        params.append('filter.p.m.custom._category1', value);
                    });

                    submissionState.middle.forEach((value) => {
                        params.append('filter.p.m.custom._category2', value);
                    });
                });

                window.location.href = url.toString();
            });
        };

        const resetCategoryFilters = () => {
            categoryForm.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
            initCategoryAccordion();

            if (isAllCollectionPage()) {
                const url = buildDirectFilterUrl((params) => {
                    params.delete('filter.p.m.custom._category1');
                    params.delete('filter.p.m.custom._category2');
                    params.delete('page');
                    params.delete('client_page');
                });
                window.location.href = url.toString();
                return;
            }

            navigateWithFacetsForm((facetsForm) => {
                const categoryLabels = new Set(
                    Array.from(categoryForm.querySelectorAll('input[data-category-level]'))
                        .map((input) => getCategoryInputLabel(input))
                        .filter(Boolean)
                );

                if (categoryLabels.size === 0) {
                    return false;
                }

                clearFacetCategoryInputs(facetsForm, categoryLabels);
            }, () => {
                const url = buildDirectFilterUrl((params) => {
                    params.delete('filter.p.m.custom._category1');
                    params.delete('filter.p.m.custom._category2');
                    params.delete('page');
                });
                window.location.href = url.toString();
            });
        };

        categoryForm.addEventListener('submit', (e) => {
            e.preventDefault();
            applyCategoryFilters();
        });

        categoryForm.querySelector('.active-button')?.addEventListener('click', applyCategoryFilters);
        categoryForm.querySelector('.reset-button')?.addEventListener('click', resetCategoryFilters);

        categoryForm._bound = true;
    }

    function getCategoryAccordionRows(categoryForm = document.getElementById('categoryForm')) {
        if (!categoryForm) return [];

        return [...categoryForm.querySelectorAll('[data-category-accordion-title]')].map((title) => {
            const toggle = title.querySelector('.category-accordion__toggle');
            const toggleText = toggle?.querySelector('.category-accordion__toggle-text');
            const panel = title.nextElementSibling?.matches('[data-category-accordion-panel]') ? title.nextElementSibling : null;
            return toggle && toggleText && panel ? { title, toggle, toggleText, panel } : null;
        }).filter(Boolean);
    }

    function setAccordionRowOpenState(row, shouldOpen) {
        row.toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        row.toggleText.textContent = shouldOpen ? '−' : '＋';
        row.title.dataset.accordionOpen = shouldOpen ? 'true' : 'false';
        row.panel.hidden = !shouldOpen;
    }

    function setCategoryAccordionOpen(targetTitle, shouldOpen, categoryForm = document.getElementById('categoryForm')) {
        if (!categoryForm) return;

        const isMobile = window.innerWidth < 750;
        const isCollapsible = categoryForm.dataset.accordionCollapsible === 'true' || isMobile;
        if (!isCollapsible) return;

        const rows = getCategoryAccordionRows(categoryForm);
        const singleOpen = categoryForm.dataset.accordionSingleOpen === 'true';

        rows.forEach((row) => {
            const isTarget = row.title === targetTitle;
            const nextOpenState = isTarget ? shouldOpen : (singleOpen && shouldOpen ? false : row.title.dataset.accordionOpen === 'true');
            setAccordionRowOpenState(row, nextOpenState);
        });
    }

    function initCategoryAccordion(categoryForm = document.getElementById('categoryForm')) {
        if (!categoryForm) return;

        const isMobile = window.innerWidth < 750;
        const modal = categoryForm.closest('.modal');
        const modalContent = modal?.querySelector('.modal-content');
        const accordion = categoryForm.querySelector('.category-accordion');
        const rows = getCategoryAccordionRows(categoryForm);
        const set = (el, prop, value) => el?.style.setProperty(prop, value, 'important');
        const clear = (el, prop) => el?.style.removeProperty(prop);
        const isCollapsible = categoryForm.dataset.accordionCollapsible === 'true' || isMobile;

        if (isMobile) {
            set(modal, 'align-items', 'flex-start');
            set(modal, 'overflow-y', 'auto');
            set(modalContent, 'height', 'auto');
            set(modalContent, 'min-height', '100vh');
            set(modalContent, 'max-height', 'none');
            set(modalContent, 'overflow', 'visible');
            set(accordion, 'overflow', 'visible');
        } else {
            [
                [modal, ['align-items', 'overflow-y']],
                [modalContent, ['height', 'min-height', 'max-height', 'overflow']],
                [accordion, ['overflow']],
            ].forEach(([el, props]) => props.forEach((prop) => clear(el, prop)));
        }

        rows.forEach(({ title, toggle, toggleText, panel }, index) => {
            const hasSelection = () => {
                const largeChecked = !!title.querySelector('input[data-category-level="large"]')?.checked;
                const middleChecked = !!panel.querySelector('input[data-category-level="middle"]:checked');
                return largeChecked || middleChecked;
            };

            if (isCollapsible) {
                set(title, 'justify-content', 'space-between');
                set(title, 'gap', '12px');
                set(title, 'margin', '0');
                set(title, 'padding', '12px 16px');
                set(toggle, 'display', 'inline-flex');
                set(toggle, 'align-items', 'center');
                set(toggle, 'justify-content', 'center');
                set(toggle, 'width', '18px');
                set(toggle, 'height', '18px');
                set(toggle, 'padding', '0');
                set(toggle, 'border', '0');
                set(toggle, 'background', 'transparent');
                set(toggle, 'color', '#5f7182');
                set(toggle, 'font-size', '24px');
                set(toggle, 'line-height', '1');
                set(toggle, 'cursor', 'pointer');
                set(toggle, 'flex-shrink', '0');
                if (categoryForm.id === 'categoryForm') {
                    set(panel, 'padding', '0 16px 12px 44px');
                    set(panel, 'margin-bottom', '0');
                }

                if (!title.dataset.accordionOpenInitialized) {
                    const shouldOpenInitially = hasSelection();
                    title.dataset.accordionOpen = shouldOpenInitially ? 'true' : 'false';
                    title.dataset.accordionOpenInitialized = 'true';
                }

                const isOpen = title.dataset.accordionOpen === 'true';
                setAccordionRowOpenState({ title, toggle, toggleText, panel }, isOpen);

                if (!toggle._accordionBound) {
                    toggle.addEventListener('click', (event) => {
                        event.stopPropagation();
                        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
                        setCategoryAccordionOpen(title, !isExpanded, categoryForm);
                    });
                    toggle._accordionBound = true;
                }

                if (!title._accordionTitleBound && !title.querySelector('input[type="checkbox"]')) {
                    title.addEventListener('click', (event) => {
                        if (event.target.closest('.category-accordion__toggle')) return;
                        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
                        setCategoryAccordionOpen(title, !isExpanded, categoryForm);
                    });
                    title._accordionTitleBound = true;
                }
            } else {
                title.dataset.accordionOpen = 'true';
                ['justify-content', 'gap', 'margin', 'padding'].forEach((prop) => clear(title, prop));
                ['display', 'align-items', 'justify-content', 'width', 'height', 'padding', 'border', 'background', 'color', 'font-size', 'line-height', 'cursor', 'flex-shrink'].forEach((prop) => clear(toggle, prop));
                ['padding', 'margin-bottom'].forEach((prop) => clear(panel, prop));
                setAccordionRowOpenState({ title, toggle, toggleText, panel }, true);
            }
        });
    }

    // フィルター機能 リダイレクト後のチェック項目を復元させる関数
    function restoreFilter() {
        // 価格絞り込みモーダル
        const tagPriceform = document.getElementById('tagPriceForm');
        const categoryForm = document.getElementById('categoryForm');

        const params = new URLSearchParams(window.location.search);

        const minPrice = params.get('filter.v.price.gte');
        const maxPrice = params.get('filter.v.price.lte');
        if (tagPriceform) {
            if (minPrice) tagPriceform.elements["filter.v.price.gte"].value = minPrice;
            if (maxPrice) tagPriceform.elements["filter.v.price.lte"].value = maxPrice;
        }

        // カテゴリモーダルの復元
        const { large, middle } = getCategoryParamValues(params);

        large.forEach(value => {
            const input = document.querySelector(`#categoryForm input[data-category-level="large"][value="${CSS.escape(value)}"]`);
            if (input) input.checked = true;
        });

        middle.forEach(value => {
            const input = document.querySelector(`#categoryForm input[data-category-level="middle"][value="${CSS.escape(value)}"]`);
            if (input) input.checked = true;
        });

        if (categoryForm) {
            syncCategoryCheckboxTree(categoryForm);
        }
    }

    document.addEventListener('facet:update', () => {
        const originalScrollIntoView = Element.prototype.scrollIntoView;
        Element.prototype.scrollIntoView = function () {
            if (this.classList.contains('collection')) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            originalScrollIntoView.apply(this, arguments);
        };

        setTimeout(() => {
            initCollectionInteractions({ includeActiveTags: true });
            applyClientSideCollectionFilters();
            window.MonotomoiCustomCollectionFilters?.syncCategoryCounts();
        }, 1000);
    });

})();
